import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Stack, Text, TextInput, PasswordInput, Button, Anchor, Checkbox } from '@mantine/core';
import { IconLogin, IconUserPlus } from '@tabler/icons-react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { PageIntro } from '@/components/rides/PageIntro';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { Callout } from '@/components/ui';
import { useCopy } from '@/lib/settings';

/** Turn a sign-in failure into a sentence a member can act on. */
function signInMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (/invalid|incorrect|wrong|credential|password|unauthori[sz]ed|401/.test(m)) {
    return "That email and password don't match. Please check them and try again.";
  }
  if (/locked|too many/.test(m)) return 'Too many tries. Please wait a few minutes, then try again.';
  if (/fetch|network|econn|timeout/.test(m)) return "We couldn't reach Carpool. Check your internet connection and try again.";
  return 'We couldn\'t sign you in. Please try again.';
}

/** Sign in. Honours ?redirect= so a deep link (e.g. /ride/event) survives the round trip. */
export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useEnrichedAuth();
  const { t } = useCopy();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectParam = searchParams.get('redirect');
  const redirectTo = redirectParam && redirectParam.startsWith('/') ? redirectParam : '/home';
  const redirectQuery = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.login(email.trim(), password, rememberMe);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const msg = String(err?.message || '');
      if (msg.includes('EMAIL_NOT_CONFIRMED')) {
        navigate(`/auth/confirm-email?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }
      setError(signInMessage(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rides-main">
      <PageIntro title="Sign in" lede={t('login.intro')} />
      <div className="rides-card" style={{ padding: 18 }}>
        <PlanningCentreButton redirectTo={redirectTo} />
        <form onSubmit={onSubmit} noValidate={false}>
          <Stack gap="md">
            {error && <Callout tone="bad" live>{error}</Callout>}
            <TextInput
              label="Email address"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              error={!!error || undefined}
            />
            <PasswordInput
              label="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
              error={!!error || undefined}
            />
            <Checkbox label="Keep me signed in on this phone" checked={rememberMe} onChange={(e) => setRememberMe(e.currentTarget.checked)} />
            <Button type="submit" loading={submitting} leftSection={<IconLogin size={20} />} fullWidth>Sign in</Button>
            <Anchor component={Link} to="/auth/forgot-password" ta="center" style={{ display: 'block', padding: '12px 0' }}>
              Forgot your password?
            </Anchor>
          </Stack>
        </form>
      </div>
      <Text ta="center" c="dimmed" mt="xl" mb={8}>New to Carpool?</Text>
      <Button component={Link} to={`/auth/register${redirectQuery}`} variant="default" fullWidth leftSection={<IconUserPlus size={20} />}>
        Create an account
      </Button>
    </div>
  );
}
